import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilePreview } from "@/context/FilePreviewContext";
import { PageHeader, Field } from "@/components/ui-kit";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Upload, Trash2, FileSignature, ShieldAlert, Plus, RefreshCcw } from "lucide-react";

const COMPANY_FIELDS = [
  { key: "company_name", label: "Company legal name", required: true },
  { key: "entity_description", label: "Entity description", placeholder: 'e.g. "a Minnesota limited liability company"' },
  { key: "address", label: "Company address" },
  { key: "phone", label: "Company phone" },
  { key: "compliance_email", label: "Compliance contact email", type: "email" },
  { key: "compliance_phone", label: "Compliance contact phone" },
  { key: "payer_name", label: "Primary payer name", placeholder: "the contracted payer" },
  { key: "payer_hotline", label: "Payer fraud hotline", placeholder: "the payer's fraud hotline" },
  { key: "arbitration_venue", label: "Arbitration venue", placeholder: "county and state" },
  { key: "notice_period", label: "Termination notice period", placeholder: "two weeks" },
  { key: "signer_name", label: "Company signer's printed name" },
  { key: "signer_title", label: "Company signer's title" },
];

const EMPTY_COMPANY = COMPANY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

export default function SettingsAgreements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openPreview } = useFilePreview();
  const canEdit = user?.role === "agency_admin";

  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingCustom, setAddingCustom] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const icFileRef = React.useRef(null);
  const customFileRef = React.useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/agency/agreements")
      .then((r) => {
        setCompany({ ...EMPTY_COMPANY, ...r.data.company });
        setItems(r.data.items || []);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const standardItems = items.filter((i) => i.kind === "standard");
  const customItems = items.filter((i) => i.kind === "custom" && i.doc_type !== "ic_agreement");
  const icReplacement = items.find((i) => i.kind === "custom" && i.doc_type === "ic_agreement");

  const save = async () => {
    setSaving(true);
    const versionsBefore = Object.fromEntries(items.map((i) => [i.key, i.version]));
    try {
      const { data } = await api.put("/agency/agreements", { company });
      setCompany({ ...EMPTY_COMPANY, ...data.company });
      setItems(data.items || []);
      const bumped = (data.items || []).some((i) => i.kind === "standard" && versionsBefore[i.key] && versionsBefore[i.key] !== i.version);
      toast.success(bumped ? "Saved. Drivers who signed the previous version will be asked to sign again." : "Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (key, enabled) => {
    try {
      const { data } = await api.put("/agency/agreements", { items: [{ key, enabled }] });
      setItems(data.items || []);
      toast.success(enabled ? "Turned on" : "Turned off");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const previewItem = (item) => {
    openPreview({ kind: "agreement", id: item.key, meta: { label: item.title } });
  };

  const uploadCustom = async (file, { title, doc_type }) => {
    const fd = new FormData();
    fd.append("title", title);
    if (doc_type) fd.append("doc_type", doc_type);
    fd.append("file", file);
    const { data } = await api.post("/agency/agreements/custom", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setItems(data.items || []);
    return data;
  };

  const addCustom = async (file) => {
    if (!file) return;
    const title = (newTitle || "").trim();
    if (title.length < 2) return toast.error("Give the agreement a title first");
    try {
      await uploadCustom(file, { title });
      setNewTitle("");
      setAddingCustom(false);
      toast.success("Agreement added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const replaceIc = async (file) => {
    if (!file) return;
    try {
      const data = await uploadCustom(file, { title: "Independent Contractor Agreement (your PDF)", doc_type: "ic_agreement" });
      const stdIc = (data.items || []).find((i) => i.key === "ic" && i.kind === "standard");
      if (stdIc && stdIc.enabled) {
        const r2 = await api.put("/agency/agreements", { items: [{ key: "ic", enabled: false }] });
        setItems(r2.data.items || []);
      }
      toast.success("Your IC agreement is now in use instead of the template");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeIcReplacement = async () => {
    if (!icReplacement) return;
    if (!window.confirm("Remove your uploaded IC agreement? The template version will be turned back on.")) return;
    try {
      await api.delete(`/agency/agreements/${icReplacement.key}`);
      const r2 = await api.put("/agency/agreements", { items: [{ key: "ic", enabled: true }] });
      setItems(r2.data.items || []);
      toast.success("Removed. The template IC agreement is on again.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeCustom = async (item) => {
    if (!window.confirm(`Remove "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/agency/agreements/${item.key}`);
      setItems((prev) => prev.filter((i) => i.key !== item.key));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="settings-agreements-page">
      <PageHeader
        subtitle="Configuration"
        title="Agreements"
        action={
          <button onClick={() => navigate("/app/settings")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1.5" data-testid="agreements-back">
            <ArrowLeft size={14} /> Back to Alert Rules
          </button>
        }
      />

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="max-w-3xl space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="font-display font-semibold text-slate-900 mb-1">Company facts</div>
            <div className="text-xs text-slate-500 mb-4">
              These fill in the two standard agreement templates below. Every driver sees the same facts on the version they sign.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {COMPANY_FIELDS.map((f) => (
                <Field
                  key={f.key}
                  label={f.label + (f.required ? " *" : "")}
                  testId={`agr-company-${f.key}`}
                  type={f.type || "text"}
                  value={company[f.key]}
                  placeholder={f.placeholder}
                  disabled={!canEdit}
                  onChange={(e) => setCompany({ ...company, [f.key]: e.target.value })}
                />
              ))}
            </div>
            {canEdit && (
              <div className="mt-5 flex justify-end">
                <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-1.5 disabled:opacity-60" data-testid="agr-save-company">
                  <Save size={14} /> {saving ? "Saving…" : "Save company facts"}
                </button>
              </div>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs text-amber-900 flex items-start gap-2" data-testid="agr-guardrail">
            <ShieldAlert size={16} className="flex-shrink-0 mt-0.5" />
            <div>Template provided for convenience, not legal advice. Have your attorney review before use.</div>
          </div>

          <div className="space-y-3">
            {standardItems.map((item) => (
              <div key={item.key} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`agr-item-${item.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
                      <FileSignature size={15} className="text-[#005E82]" /> {item.title}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Version {item.version}</div>
                  </div>
                  {canEdit && (
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={(e) => toggleEnabled(item.key, e.target.checked)}
                        data-testid={`agr-toggle-${item.key}`}
                      />
                      Enabled
                    </label>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button onClick={() => previewItem(item)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5" data-testid={`agr-preview-${item.key}`}>
                    <Eye size={13} /> Preview PDF
                  </button>
                  {item.key === "ic" && canEdit && !icReplacement && (
                    <>
                      <button onClick={() => icFileRef.current?.click()} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5" data-testid="agr-replace-ic">
                        <Upload size={13} /> Replace with our own PDF
                      </button>
                      <input ref={icFileRef} type="file" accept="application/pdf" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) replaceIc(f); }} />
                    </>
                  )}
                </div>
                {item.key === "ic" && icReplacement && (
                  <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center justify-between gap-3">
                    <span>Your own PDF (&quot;{icReplacement.title}&quot;) is in use instead of this template.</span>
                    {canEdit && (
                      <button onClick={removeIcReplacement} className="text-rose-600 hover:underline flex items-center gap-1 flex-shrink-0" data-testid="agr-remove-ic-replacement">
                        <RefreshCcw size={12} /> Use template instead
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold text-slate-900">Other agreements</div>
              {canEdit && !addingCustom && (
                <button onClick={() => setAddingCustom(true)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5" data-testid="agr-add-custom-btn">
                  <Plus size={13} /> Add another agreement
                </button>
              )}
            </div>

            {customItems.length === 0 && !addingCustom && (
              <div className="text-xs text-slate-400">No custom agreements yet.</div>
            )}

            <div className="space-y-2">
              {customItems.map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-3 border border-slate-100 rounded-md px-3 py-2" data-testid={`agr-custom-${item.key}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 truncate">{item.title}</div>
                    <div className="text-[11px] text-slate-500">{item.custom_filename}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canEdit && (
                      <label className="flex items-center gap-1.5 text-xs text-slate-600">
                        <input type="checkbox" checked={item.enabled} onChange={(e) => toggleEnabled(item.key, e.target.checked)} data-testid={`agr-toggle-${item.key}`} />
                        Enabled
                      </label>
                    )}
                    <button onClick={() => previewItem(item)} className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50" data-testid={`agr-preview-${item.key}`}>
                      <Eye size={13} />
                    </button>
                    {canEdit && (
                      <button onClick={() => removeCustom(item)} className="text-xs px-2.5 py-1.5 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`agr-remove-${item.key}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {addingCustom && (
              <div className="mt-4 border-t border-slate-100 pt-4 space-y-3">
                <Field label="Title" testId="agr-new-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Vendor Services Agreement" />
                <div className="flex items-center gap-2">
                  <button onClick={() => customFileRef.current?.click()} className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5" data-testid="agr-upload-custom">
                    <Upload size={13} /> Choose PDF and add
                  </button>
                  <button onClick={() => { setAddingCustom(false); setNewTitle(""); }} className="text-xs px-3 py-2 text-slate-500 hover:text-slate-800">
                    Cancel
                  </button>
                  <input ref={customFileRef} type="file" accept="application/pdf" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) addCustom(f); }} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
